import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { staffService } from '../../services';
import { ArrowLeft, AlertCircle, Mail, Phone, Calendar, Clock, CheckCircle2, User, Briefcase } from 'lucide-react';
import { Loader } from '../../components';

/**
 * Care Home Manager - Employee Details Page
 * Display detailed employee information and shift history
 */
const EmployeeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  const [employee, setEmployee] = useState(null);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEmployeeDetails();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      setLoading(true);
      const response = await staffService.getStaffById(id);
      if (response.success) {
        setEmployee(response.data.employee);
        setShiftHistory(response.data.shiftHistory || []);
        setError('');
      } else {
        setError(response.message || 'Failed to load employee details');
      }
    } catch (err) {
      console.error('Error fetching employee details:', err);
      setError('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Prefer the server's total_hours (already the sum of every session minus
  // break time). Fall back to start/end span for legacy rows that don't have
  // it yet — but warn that the fallback over-counts multi-session days.
  const calculateShiftDuration = (shift) => {
    if (shift && typeof shift.total_hours !== 'undefined' && shift.total_hours !== null) {
      const th = parseFloat(shift.total_hours);
      if (!Number.isNaN(th)) {
        const h = Math.floor(th);
        const m = Math.round((th - h) * 60);
        return `${h}h ${m}m`;
      }
    }
    if (!shift?.start_time || !shift?.end_time) return 'N/A';
    const diffMs = new Date(shift.end_time) - new Date(shift.start_time);
    const breakMs = (shift.break_time_minutes || 0) * 60 * 1000;
    const netMs = Math.max(0, diffMs - breakMs);
    const hours = Math.floor(netMs / (1000 * 60 * 60));
    const minutes = Math.floor((netMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getApprovalStatusBadge = (status) => {
    const statusClass = {
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusClass[status] || statusClass.pending}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </span>
    );
  };

  if (loading) {
    return <Loader text="Loading employee details..." />;
  }

  if (!employee) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/manager/staffs')}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Staffs
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error || 'Employee not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/manager/staffs')}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Staffs
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{employee.name}</h1>
          <p className="text-gray-600 mt-1">Employee ID: {employee.user_id}</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Employee Information Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Employee Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <div className="mt-1 flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{employee.name}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="mt-1 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{employee.email}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <div className="mt-1 flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{employee.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Status Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Approval Status</label>
              <div className="mt-1">
                {getApprovalStatusBadge(employee.approval_status)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Active Status</label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  employee.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {employee.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Member Since</label>
              <div className="mt-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{formatDate(employee.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shift History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Shift History</h2>
          <p className="text-gray-600 text-sm mt-1">Last 30 shifts</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Shift Date
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  End Time
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Break Time
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {shiftHistory.length > 0 ? (
                shiftHistory.map((shift) => (
                  <tr key={shift.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(shift.start_time)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {formatTime(shift.start_time)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {shift.end_time ? formatTime(shift.end_time) : 'Ongoing'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {shift.end_time ? calculateShiftDuration(shift) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {shift.break_time_minutes ? `${shift.break_time_minutes} min` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {shift.notes || '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No shifts recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Shift Summary */}
        {shiftHistory.length > 0 && (
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Shifts</p>
                <p className="text-2xl font-bold text-blue-600">{shiftHistory.length}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Ongoing Shifts</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {shiftHistory.filter(s => !s.end_time).length}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Completed Shifts</p>
                <p className="text-2xl font-bold text-green-600">
                  {shiftHistory.filter(s => s.end_time).length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDetails;
